import React, { useEffect, useState } from 'react'
import Checkbox from '../checkbox'
import Input, { TTheme } from '../input'
import { SelectTagStyles as s } from './styles'

interface SelectProps {
  'data-qa'?: string
  label: string
  options: IOption[]
  defaultSelectedOptions?: IOption[]
  onChange: (selectedOptions: IOption[]) => void
  inputValue?: (text: string) => void
  disabled?: boolean
  hint?: string
  theme?: TTheme
  placeholder?: string
  emptyLabel?: string
}

interface IOption {
  key: string | number
  value: string
}

const SelectTag: React.FC<SelectProps> = (props: SelectProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<IOption[]>()
  const [inputValue, setInputValue] = useState<string>('')
  const listRef = React.useRef<HTMLUListElement>(null)
  const [selectedOptions, setSelectedOptions] = React.useState<IOption[]>(
    props.defaultSelectedOptions || [],
  )

  useEffect(() => {
    setSuggestions(
      props.options.filter((option) => !selectedOptions.includes(option)),
    )
  }, [props.options])

  useEffect(() => {
    props.onChange && props.onChange(selectedOptions)
    onChangeInput(inputValue)
  }, [selectedOptions])

  useEffect(() => {
    onChangeInput(inputValue)
    props.inputValue && props.inputValue(inputValue)
  }, [inputValue])

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true)
    return () => document.removeEventListener('click', handleClickOutside, true)
  }, [])

  function handleClickOutside(event: any) {
    if (!listRef.current?.contains(event.target)) setIsOpen(false)
  }

  function addSelectedLine(option: IOption) {
    setSelectedOptions((state) => [...state, option])
  }

  function removeSelectedLine(index: number) {
    const updatedOptions = [...selectedOptions]
    updatedOptions.splice(index, 1)
    setSelectedOptions(updatedOptions)
  }

  function getInputValue() {
    let value = `${selectedOptions.length} selecionado`
    if (selectedOptions.length > 1) value += 's'
    return !isOpen && selectedOptions.length ? value : ''
  }

  function onChangeInput(value: string) {
    const filteredOptions = props.options.filter(
      (option) =>
        option.value.toLowerCase().includes(value.toLowerCase()) &&
        !selectedOptions.includes(option),
    )
    setSuggestions(filteredOptions)
  }

  return (
    <s.selectWrapper
      className={isOpen ? 'open' : ''}
      data-qa={props['data-qa']}
    >
      <s.inputWrapper
        className={isOpen ? 'open' : ''}
        onClick={() => !props.disabled && setIsOpen(!isOpen)}
      >
        <Input
          label={props.label}
          value={getInputValue()}
          onChange={setInputValue}
          icon="keyboard_arrow_down"
          disabled={props.disabled}
          placeholder={
            isOpen ? '' : props.placeholder || 'Selecione um ou mais...'
          }
          theme={isOpen ? 'enabled' : props.theme}
          hint={isOpen ? '' : props.hint}
          data-qa={props['data-qa'] && `${props['data-qa']}-input`}
        />
      </s.inputWrapper>
      <s.listContainer data-qa={props['data-qa'] && `${props['data-qa']}-list`}>
        {isOpen && !props.disabled && (
          <ul ref={listRef}>
            {!inputValue &&
              selectedOptions.map((option, index) => (
                <s.listItem
                  key={option.key}
                  data-qa={`${props['data-qa']}-selected-${option.key}`}
                >
                  <label>
                    <Checkbox
                      defaultChecked={true}
                      onChange={(value) => !value && removeSelectedLine(index)}
                    />
                    <span>{option.value}</span>
                  </label>
                </s.listItem>
              ))}
            {suggestions.map((option) => (
              <s.listItem
                key={option.key}
                data-qa={`${props['data-qa']}-suggestion-${option.key}`}
              >
                <label>
                  <Checkbox
                    onChange={(value) => value && addSelectedLine(option)}
                  />
                  <span>{option.value}</span>
                </label>
              </s.listItem>
            ))}
            {(inputValue || !props.options.length) && !suggestions.length && (
              <li
                className="empty-list"
                onClick={() => setIsOpen(!isOpen)}
                data-qa={props['data-qa'] && `${props['data-qa']}-empty`}
              >
                {props.emptyLabel}
              </li>
            )}
          </ul>
        )}
      </s.listContainer>
    </s.selectWrapper>
  )
}

SelectTag.defaultProps = {
  emptyLabel: 'Sem registros',
}

export default SelectTag
